// ConsumerAuthShell — consumer-portal wrapper around <AuthLayout>.
//
// Parallel to packages/admin/src/auth/AdminAuthShell.tsx. The two shells
// exist so the auth-screen components don't have to know which portal
// they're in — they just compose `<ConsumerAuthShell>{form content}` /
// `<AdminAuthShell>{form content}` and the right chrome lands.
//
// Portal selection is automatic: the consumer SPA is wrapped in
// `<PortalProvider portal="consumer">` at the root (see App.tsx), so
// AuthLayout reads portal from context. We don't pass `portal="consumer"`
// explicitly here — that would re-introduce the prop-threading pattern
// this shell exists to avoid.
//
// Differences vs AdminAuthShell:
// - No utility bar (admin-only chrome per designer spec)
// - No monoHeadline (consumer headline is display weight, warm editorial)
// - No wordmarkBadge (no ADMIN pill)
// - 'dots' pattern (consumer warm; admin forces 'grid')

import type { ReactNode } from 'react';
import { AuthLayout } from '@gc-coach/design-system';
import { useI18n } from '../i18n/index.js';

export interface ConsumerAuthShellProps {
  children: ReactNode;
}

export function ConsumerAuthShell({ children }: ConsumerAuthShellProps) {
  const { t } = useI18n();

  return (
    <AuthLayout pattern="dots">
      <AuthLayout.Brand
        wordmark={t('brand.wordmark')}
        wordmarkSub={t('brand.wordmarkSub')}
        eyebrow={t('brand.eyebrow')}
        headline={t('brand.headline')}
        tagline={t('brand.tagline')}
        footerLeft={t('brand.footerLeft')}
        footerRight={t('brand.footerRight')}
      />
      <AuthLayout.Form>{children}</AuthLayout.Form>
    </AuthLayout>
  );
}
